import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Thermometer, Plus, Send, Ban, Trash2, Lock, BarChart3, ClipboardEdit, Wrench } from 'lucide-react';
import {
  CampagneDetail,
  CampagneRij,
  annuleerCampagne,
  getCampagne,
  getCampagnes,
  getHittekaart,
  getHittekaartBronnen,
  getOpenAntwoorden,
  planCampagne,
  verwijderCampagne,
} from '../../services/dataCockpitApi';
import { usePeriode } from '../../store/dataPeriodeStore';
import { DataFilterBalk, DataKop, Kaart, Laden, Fout } from '../../components/data/DataFilterBalk';
import { CampagneWizard } from '../../components/data/CampagneWizard';

/** Zelfde kleurbanden als de Bedrijfsthermometer in het medewerkersportaal. */
const scoreKleur = (score: number): string => {
  if (score <= 2) return 'bg-red-200 text-red-900';
  if (score < 3) return 'bg-amber-200 text-amber-900';
  if (score < 4) return 'bg-lime-200 text-lime-900';
  return 'bg-green-300 text-green-900';
};

const STATUS_KLASSE: Record<string, string> = {
  CONCEPT: 'bg-amber-50 text-amber-800',
  GEPLAND: 'bg-blue-50 text-blue-800',
  LIVE: 'bg-green-50 text-green-800',
  AFGESLOTEN: 'bg-green-100 text-green-900',
  GEANNULEERD: 'bg-red-50 text-red-700',
};
const STATUS_ICOON: Record<string, string> = { CONCEPT: '⏳', GEPLAND: '🔵', LIVE: '🟢', AFGESLOTEN: '🟢', GEANNULEERD: '⛔' };

type Keuze = { waarde: string; zet: (v: string) => void };

const datum = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

/** Live Regie-Dashboard: alle campagnes met type, verzenddatum, status, respons en de juiste vervolgactie. */
const Campagnes: React.FC<{ bekijk: (id: string) => void }> = ({ bekijk }) => {
  const { t } = useTranslation('employer');
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['data-campagnes'], queryFn: getCampagnes });
  const [wizard, setWizard] = useState<{ open: boolean; bestaand: CampagneDetail | null }>({ open: false, bestaand: null });

  const actie = useMutation({
    mutationFn: async ({ soort, id }: { soort: 'plannen' | 'annuleren' | 'verwijderen'; id: string }) =>
      soort === 'plannen' ? planCampagne(id) : soort === 'annuleren' ? annuleerCampagne(id) : verwijderCampagne(id),
    onSuccess: (_d, v) => {
      toast.success(t(`dataCockpit.campagnes.ok.${v.soort}`));
      qc.invalidateQueries({ queryKey: ['data-campagnes'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || t('dataCockpit.fout')),
  });
  const bewerk = async (c: CampagneRij) => {
    try {
      setWizard({ open: true, bestaand: await getCampagne(c.id) });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('dataCockpit.fout'));
    }
  };

  if (q.isLoading) return <Laden />;
  if (q.isError) return <Fout fout={q.error} />;
  const d = q.data!;
  if (!d.module_actief) {
    return <Kaart titel={t('dataCockpit.campagnes.titel')}><p className="text-sm text-gray-500">{t('dataCockpit.campagnes.nietActief')}</p></Kaart>;
  }
  const bewerkbaar = (c: CampagneRij) => c.status === 'CONCEPT' || (c.status === 'GEPLAND' && (!c.start_op || new Date(c.start_op) > new Date()));

  return (
    <Kaart
      titel={t('dataCockpit.campagnes.titel')}
      uitleg={t('dataCockpit.campagnes.uitleg')}
      actie={
        d.mag_beheren && (
          <button type="button" onClick={() => setWizard({ open: true, bestaand: null })} className="inline-flex items-center gap-1.5 rounded-lg bg-[#0B1F3A] px-3 py-2 text-sm font-semibold text-white">
            <Plus className="h-4 w-4" /> {t('dataCockpit.campagnes.nieuw')}
          </button>
        )
      }
    >
      {d.campagnes.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500">{t('dataCockpit.campagnes.geen')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                <th className="py-2 pr-3 font-medium">{t('dataCockpit.campagnes.kolCampagne')}</th>
                <th className="py-2 pr-3 font-medium">{t('dataCockpit.campagnes.kolType')}</th>
                <th className="py-2 pr-3 font-medium">{t('dataCockpit.campagnes.kolVerzenddatum')}</th>
                <th className="py-2 pr-3 font-medium">{t('dataCockpit.campagnes.kolStatus')}</th>
                <th className="py-2 pr-3 font-medium">{t('dataCockpit.campagnes.kolRespons')}</th>
                <th className="py-2 text-right font-medium">{t('dataCockpit.campagnes.kolActies')}</th>
              </tr>
            </thead>
            <tbody>
              {d.campagnes.map((c) => (
                <tr key={c.id} className="border-b border-gray-100 align-top">
                  <td className="py-2.5 pr-3">
                    <p className="font-medium text-gray-900">{c.titel}</p>
                    <p className="text-xs text-gray-500">{c.doelgroep}</p>
                  </td>
                  <td className="py-2.5 pr-3 whitespace-nowrap text-xs text-gray-700">
                    {c.soort === 'STANDAARD' ? `🛡️ ${t('dataCockpit.campagnes.typeStandaard')}` : `📐 ${t('dataCockpit.campagnes.typeCustom', { n: c.aantal_vragen })}`}
                  </td>
                  <td className="py-2.5 pr-3 whitespace-nowrap text-xs text-gray-600">{datum(c.start_op)}</td>
                  <td className="py-2.5 pr-3 whitespace-nowrap">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_KLASSE[c.status]}`}>
                      <span aria-hidden>{STATUS_ICOON[c.status]} </span>{t(`dataCockpit.campagnes.status.${c.status}`)}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3 whitespace-nowrap text-xs text-gray-600">
                    {c.respons_pct == null ? '—' : <><strong className="text-gray-900">{c.respons_pct}%</strong> <span className="text-gray-400">({c.deelnames}/{c.doelgroep_omvang})</span></>}
                  </td>
                  <td className="py-2.5 text-right whitespace-nowrap">
                    <div className="inline-flex flex-wrap justify-end gap-1">
                      {c.status === 'AFGESLOTEN' && (
                        <button type="button" onClick={() => bekijk(c.id)} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 text-xs font-medium text-[#0B1F3A] hover:bg-blue-50">
                          <BarChart3 className="h-3.5 w-3.5" aria-hidden /> {t('dataCockpit.campagnes.bekijkHittekaart')}
                        </button>
                      )}
                      {d.mag_beheren && bewerkbaar(c) && (
                        <button type="button" onClick={() => bewerk(c)} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                          {c.status === 'CONCEPT' ? <Wrench className="h-3.5 w-3.5" aria-hidden /> : <ClipboardEdit className="h-3.5 w-3.5" aria-hidden />}
                          {c.status === 'CONCEPT' ? t('dataCockpit.campagnes.ontwerpAfronden') : t('dataCockpit.campagnes.inrichten')}
                        </button>
                      )}
                      {d.mag_beheren && c.status === 'CONCEPT' && (
                        <>
                          <button type="button" onClick={() => actie.mutate({ soort: 'plannen', id: c.id })} className="rounded p-1.5 text-[#0B1F3A] hover:bg-blue-50" title={t('dataCockpit.campagnes.inplannen')} aria-label={t('dataCockpit.campagnes.inplannen')}><Send className="h-4 w-4" /></button>
                          <button type="button" onClick={() => window.confirm(t('dataCockpit.campagnes.bevestigVerwijderen')) && actie.mutate({ soort: 'verwijderen', id: c.id })} className="rounded p-1.5 text-red-600 hover:bg-red-50" title={t('dataCockpit.campagnes.verwijderen')} aria-label={t('dataCockpit.campagnes.verwijderen')}><Trash2 className="h-4 w-4" /></button>
                        </>
                      )}
                      {d.mag_beheren && (c.status === 'GEPLAND' || c.status === 'LIVE') && (
                        <button type="button" onClick={() => window.confirm(t('dataCockpit.campagnes.bevestigAnnuleren')) && actie.mutate({ soort: 'annuleren', id: c.id })} className="rounded p-1.5 text-red-600 hover:bg-red-50" title={t('dataCockpit.campagnes.annuleren')} aria-label={t('dataCockpit.campagnes.annuleren')}><Ban className="h-4 w-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {wizard.open && <CampagneWizard bestaand={wizard.bestaand} onSluit={() => setWizard({ open: false, bestaand: null })} />}
    </Kaart>
  );
};

const HittekaartKaart: React.FC<{ keuze: Keuze }> = ({ keuze }) => {
  const { t } = useTranslation('employer');
  const { periode, key } = usePeriode();
  const bronnen = useQuery({ queryKey: ['data-cultuur-bronnen', ...key], queryFn: () => getHittekaartBronnen(periode) });
  const { waarde: gekozen, zet: setGekozen } = keuze;
  // Zonder keuze: de nieuwste bron in de periode. Een campagne die via het
  // dashboard is gekozen blijft staan, ook als hij buiten de periode valt.
  useEffect(() => {
    const lijst = bronnen.data ?? [];
    if (!gekozen && lijst[0]) setGekozen(`${lijst[0].soort}:${lijst[0].id}`);
  }, [bronnen.data, gekozen, setGekozen]);
  const buitenPeriode = Boolean(gekozen) && !(bronnen.data ?? []).some((b) => `${b.soort}:${b.id}` === gekozen);
  const [soort, id] = gekozen.split(':') as ['campagne' | 'thermometer', string];
  const kaart = useQuery({ queryKey: ['data-hittekaart', gekozen], queryFn: () => getHittekaart(soort, id), enabled: Boolean(gekozen) });
  const open = useQuery({ queryKey: ['data-open', id], queryFn: () => getOpenAntwoorden(id), enabled: soort === 'campagne' && Boolean(id), retry: false });
  const h = kaart.data;

  return (
    <Kaart
      titel={t('dataCockpit.hittekaart.titel')}
      uitleg={t('dataCockpit.hittekaart.uitleg')}
      actie={
        ((bronnen.data?.length ?? 0) > 0 || buitenPeriode) && (
          <select aria-label={t('dataCockpit.hittekaart.bron')} className="max-w-xs rounded-lg border border-gray-300 px-2 py-1.5 text-sm" value={gekozen} onChange={(e) => setGekozen(e.target.value)}>
            {buitenPeriode && <option value={gekozen}>{kaart.data?.bron.titel ?? '…'}</option>}
            {(bronnen.data ?? []).map((b) => <option key={`${b.soort}:${b.id}`} value={`${b.soort}:${b.id}`}>{b.titel}</option>)}
          </select>
        )
      }
    >
      {bronnen.isLoading || kaart.isLoading ? (
        <Laden />
      ) : bronnen.isError ? (
        <Fout fout={bronnen.error} />
      ) : !gekozen ? (
        <p className="py-8 text-center text-sm text-gray-500">{t('dataCockpit.hittekaart.geenBronnen')}</p>
      ) : kaart.isError ? (
        <Fout fout={kaart.error} />
      ) : h ? (
        <>
          {h.organisatie_onderdrukt ? (
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <Lock className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
              <p>{t('dataCockpit.privacy.responses', { drempel: h.minimum_responses })}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border-separate" style={{ borderSpacing: 4 }}>
                <thead>
                  <tr>
                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500">{t('dataCockpit.hittekaart.groep')}</th>
                    {h.kolommen.map((k) => <th key={k.key} className="max-w-[10rem] px-2 py-1 text-left text-xs font-medium text-gray-500" title={k.label}><span className="line-clamp-2">{k.label}</span></th>)}
                    <th className="px-2 py-1 text-right text-xs font-medium text-gray-500">n</th>
                  </tr>
                </thead>
                <tbody>
                  {h.rijen.filter((r) => r.scores).map((r) => (
                    <tr key={r.id ?? 'org'}>
                      <td className={`whitespace-nowrap px-2 py-1 text-sm ${r.soort === 'organisatie' ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>{r.naam}</td>
                      {r.scores!.map((s, i) => (
                        <td key={i} className="px-0 py-0">
                          {s == null ? (
                            <div className="rounded-md bg-gray-100 px-3 py-2 text-center text-sm text-gray-400">—</div>
                          ) : (
                            <div className={`min-w-[4rem] rounded-md px-3 py-2 text-center text-sm font-semibold tabular-nums ${scoreKleur(s)}`}>
                              {s.toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                            </div>
                          )}
                        </td>
                      ))}
                      <td className="px-2 py-1 text-right text-xs text-gray-500">{r.aantal}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
            {[['bg-red-200', '≤ 2,0'], ['bg-amber-200', '2,0 – 3,0'], ['bg-lime-200', '3,0 – 4,0'], ['bg-green-300', '≥ 4,0']].map(([k, l]) => (
              <span key={k} className="inline-flex items-center gap-1.5"><span className={`inline-block h-3 w-3 rounded ${k}`} />{l}</span>
            ))}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            {t('dataCockpit.hittekaart.grendel', { n: h.minimum_responses })}
            {h.afdelingen_samengevoegd > 0 && ` ${t('dataCockpit.hittekaart.samengevoegd', { n: h.afdelingen_samengevoegd })}`}
          </p>

          {soort === 'campagne' && open.data && (
            <div className="mt-5 border-t border-gray-100 pt-4">
              <h3 className="mb-2 text-sm font-semibold text-gray-900">{t('dataCockpit.hittekaart.openAntwoorden')}</h3>
              {open.data.onderdrukt ? (
                <p className="text-xs text-amber-800"><Lock className="mr-1 inline h-3 w-3" aria-hidden />{t('dataCockpit.privacy.responses', { drempel: open.data.minimum_responses })}</p>
              ) : open.data.vragen.length === 0 ? (
                <p className="text-xs text-gray-500">{t('dataCockpit.hittekaart.geenOpen')}</p>
              ) : (
                open.data.vragen.map((v) => (
                  <details key={v.id} className="mb-2 rounded-lg border border-gray-200 p-3">
                    <summary className="cursor-pointer text-sm font-medium text-gray-800">{v.tekst} <span className="text-xs font-normal text-gray-500">({v.antwoorden.length})</span></summary>
                    <p className="mt-2 text-xs font-semibold text-gray-500">{t('dataCockpit.hittekaart.heleOrganisatie')}</p>
                    <ul className="mt-1 space-y-1.5">
                      {v.antwoorden.map((a, i) => <li key={i} className="rounded bg-gray-50 px-3 py-2 text-sm text-gray-700">“{a}”</li>)}
                    </ul>
                    {v.afdelingen.map((af) => (
                      <div key={af.id} className="mt-3">
                        <p className="text-xs font-semibold text-gray-500">{af.naam} <span className="font-normal">({af.antwoorden.length})</span></p>
                        <ul className="mt-1 space-y-1.5">
                          {af.antwoorden.map((a, i) => <li key={i} className="rounded bg-gray-50 px-3 py-2 text-sm text-gray-700">“{a}”</li>)}
                        </ul>
                      </div>
                    ))}
                    {v.afdelingen_vergrendeld > 0 && (
                      <p className="mt-3 text-xs text-amber-800">
                        <Lock className="mr-1 inline h-3 w-3" aria-hidden />
                        {t('dataCockpit.hittekaart.tekstGrendel', { n: v.afdelingen_vergrendeld, teksten: open.data!.minimum_teksten, responses: open.data!.minimum_responses })}
                      </p>
                    )}
                  </details>
                ))
              )}
            </div>
          )}
        </>
      ) : null}
    </Kaart>
  );
};

/** Submap 4 — Bedrijfsthermometer & Pulse Regie (Cultuur-Radar). */
export const DataCultuurPage: React.FC = () => {
  const { t } = useTranslation('employer');
  const [gekozen, setGekozen] = useState('');
  const bekijk = (id: string) => {
    setGekozen(`campagne:${id}`);
    document.getElementById('hittekaart')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <DataKop titel={t('dataCockpit.cultuur.titel')} ondertitel={t('dataCockpit.cultuur.ondertitel')} icoon={<Thermometer className="h-6 w-6" />} />
      <DataFilterBalk />
      <div className="space-y-6">
        <div id="hittekaart" className="scroll-mt-4">
          <HittekaartKaart keuze={{ waarde: gekozen, zet: setGekozen }} />
        </div>
        <Campagnes bekijk={bekijk} />
      </div>
    </div>
  );
};

export default DataCultuurPage;
